import { EdgelessEditor } from '@affine/core/blocksuite/editors/edgeless-editor';
import { PageEditor } from '@affine/core/blocksuite/editors/page-editor';
import { mountReadonlyEdgelessEdgeRuntime } from '@affine/core/modules/streamlain/edge-infrastructure/readonly-edge-runtime';
import { AttachmentViewExtension } from '@blocksuite/affine/blocks/attachment/view';
import { CalloutViewExtension } from '@blocksuite/affine/blocks/callout/view';
import { CodeBlockViewExtension } from '@blocksuite/affine/blocks/code/view';
import { DataViewViewExtension } from '@blocksuite/affine/blocks/data-view/view';
import { DatabaseViewExtension } from '@blocksuite/affine/blocks/database/view';
import { DividerViewExtension } from '@blocksuite/affine/blocks/divider/view';
import { EdgelessTextViewExtension } from '@blocksuite/affine/blocks/edgeless-text/view';
import { FrameViewExtension } from '@blocksuite/affine/blocks/frame/view';
import { ImageViewExtension } from '@blocksuite/affine/blocks/image/view';
import { LatexViewExtension as LatexBlockViewExtension } from '@blocksuite/affine/blocks/latex/view';
import { ListViewExtension } from '@blocksuite/affine/blocks/list/view';
import { NoteConfigExtension } from '@blocksuite/affine/blocks/note';
import { NoteViewExtension } from '@blocksuite/affine/blocks/note/view';
import { ParagraphViewExtension } from '@blocksuite/affine/blocks/paragraph/view';
import { RootViewExtension } from '@blocksuite/affine/blocks/root/view';
import { StreamLainViewExtension } from '@blocksuite/affine/blocks/streamlain/view';
import { SurfaceViewExtension } from '@blocksuite/affine/blocks/surface/view';
import { TableViewExtension } from '@blocksuite/affine/blocks/table/view';
import {
  type ViewExtensionContext,
  ViewExtensionManager,
  ViewExtensionProvider,
} from '@blocksuite/affine/ext-loader';
import { FoundationViewExtension } from '@blocksuite/affine/foundation/view';
import { DocTitleViewExtension } from '@blocksuite/affine/fragments/doc-title/view';
import { ConnectorViewExtension } from '@blocksuite/affine/gfx/connector/view';
import { GroupViewExtension } from '@blocksuite/affine/gfx/group/view';
import { LinkViewExtension as GfxLinkViewExtension } from '@blocksuite/affine/gfx/link/view';
import { NoteViewExtension as GfxNoteViewExtension } from '@blocksuite/affine/gfx/note/view';
import { PointerViewExtension } from '@blocksuite/affine/gfx/pointer/view';
import { ShapeViewExtension } from '@blocksuite/affine/gfx/shape/view';
import { TextViewExtension } from '@blocksuite/affine/gfx/text/view';
import { InlineCommentViewExtension } from '@blocksuite/affine/inlines/comment';
import { FootnoteViewExtension } from '@blocksuite/affine/inlines/footnote/view';
import { LatexViewExtension as InlineLatexViewExtension } from '@blocksuite/affine/inlines/latex/view';
import { LinkViewExtension } from '@blocksuite/affine/inlines/link/view';
import { MentionViewExtension } from '@blocksuite/affine/inlines/mention/view';
import { InlinePresetViewExtension } from '@blocksuite/affine/inlines/preset/view';
import { ReferenceViewExtension } from '@blocksuite/affine/inlines/reference/view';
import type { DocMode } from '@blocksuite/affine/model';
import type { EditorHost } from '@blocksuite/affine/std';
import type { Store } from '@blocksuite/affine/store';
import { html } from 'lit';
import type { CSSProperties } from 'react';
import { useEffect, useRef } from 'react';

if (typeof customElements !== 'undefined') {
  if (!customElements.get('page-editor')) {
    customElements.define('page-editor', PageEditor);
  }
  if (!customElements.get('edgeless-editor')) {
    customElements.define('edgeless-editor', EdgelessEditor);
  }
}

export interface ReadonlyDocRenderSurfaceHandle extends HTMLElement {
  host?: EditorHost | null;
  updateComplete: Promise<boolean>;
}

class ReadonlyNoteConfigViewExtension extends ViewExtensionProvider {
  override name = 'readonly-note-config-view';

  override setup(context: ViewExtensionContext) {
    super.setup(context);
    context.register(
      NoteConfigExtension({
        edgelessNoteHeader: () => html``,
        pageBlockTitle: ({ note }) =>
          html`<doc-title .doc=${note.store}></doc-title>`,
      })
    );
  }
}

const manager = new ViewExtensionManager([
  FoundationViewExtension,
  PointerViewExtension,
  GfxNoteViewExtension,
  ShapeViewExtension,
  ConnectorViewExtension,
  GroupViewExtension,
  TextViewExtension,
  GfxLinkViewExtension,
  AttachmentViewExtension,
  CalloutViewExtension,
  CodeBlockViewExtension,
  DataViewViewExtension,
  DatabaseViewExtension,
  DividerViewExtension,
  EdgelessTextViewExtension,
  FrameViewExtension,
  ImageViewExtension,
  LatexBlockViewExtension,
  ListViewExtension,
  NoteViewExtension,
  DocTitleViewExtension,
  ReadonlyNoteConfigViewExtension,
  ParagraphViewExtension,
  StreamLainViewExtension,
  SurfaceViewExtension,
  TableViewExtension,
  RootViewExtension,
  InlineCommentViewExtension,
  FootnoteViewExtension,
  LinkViewExtension,
  ReferenceViewExtension,
  InlineLatexViewExtension,
  MentionViewExtension,
  InlinePresetViewExtension,
]);

export function ReadonlyDocRenderSurface({
  page,
  mode,
  className,
  style,
  onReady,
}: {
  page: Store;
  mode: DocMode;
  className?: string;
  style?: CSSProperties;
  onReady?: (editor: ReadonlyDocRenderSurfaceHandle) => void | (() => void);
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const editor =
      mode === 'edgeless' ? new EdgelessEditor() : new PageEditor();
    editor.doc = page;
    editor.specs = manager.get(mode);
    editor.style.display = 'block';
    editor.style.width = '100%';
    editor.style.height = '100%';
    editor.setAttribute('data-sl-readonly-render-surface', mode);
    container.replaceChildren(editor);
    let cancelled = false;
    let disposeReady: void | (() => void);
    let disposeEdges: void | (() => void);
    void editor.updateComplete.then(() => {
      if (cancelled) return;
      if (mode === 'edgeless') {
        disposeEdges = mountReadonlyEdgelessEdgeRuntime({
          editor,
          store: page,
        });
      }
      disposeReady = onReady?.(editor);
    });
    return () => {
      cancelled = true;
      disposeReady?.();
      disposeEdges?.();
      editor.remove();
    };
  }, [mode, onReady, page]);
  return <div ref={containerRef} className={className} style={style} />;
}
